import { useRef, useState } from 'react';

import { Alert } from '@mui/material';

import type { RelatedClientRecord } from '@/apps/engagement-letter-database/data/types';
import { useAddEngagementCommentMutation } from '@/gql/operations';
import { BaseModal } from '@/shared/components/Modal';

import { CommentForm } from '../CommentForm';
import type { CommentFormData } from '../CommentForm/types';
import SelectedClientsList from '../SelectedClientsList';

interface CommentModalProps {
  selectedClients: RelatedClientRecord['Clients'];
  currentUserMpId: string;
  onClose: () => void;
}

const CommentModal = ({ selectedClients, currentUserMpId, onClose }: CommentModalProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedRecordIds, setSavedRecordIds] = useState(new Set<string>());
  const saving = useRef(false);
  const [addComment] = useAddEngagementCommentMutation({ refetchQueries: ['GetFilteredClientRecords'] });

  const handleSave = async (formData: CommentFormData) => {
    if (saving.current) return;
    const recordIds = [...new Set(selectedClients.flatMap((client) => client.cmap_clm_engagement_record?.id ?? []))];
    if (
      !currentUserMpId ||
      !recordIds.length ||
      selectedClients.some((client) => !client.cmap_clm_engagement_record?.id)
    ) {
      setError(
        'Unable to save comment: a current user and an engagement record for every selected client are required.',
      );
      return;
    }
    if (!formData.text?.trim()) {
      setError('Enter a comment before saving.');
      return;
    }

    saving.current = true;
    setIsSaving(true);
    setError(null);
    const completed = new Set(savedRecordIds);
    try {
      const results = await Promise.allSettled(
        recordIds
          .filter((recordId) => !completed.has(recordId))
          .map(async (recordId) => {
            const result = await addComment({
              variables: {
                engagement_records_id: recordId,
                comments: formData.text,
                created_by: currentUserMpId,
                updated_by: currentUserMpId,
              },
            });
            if (!result.data?.insert_clm_engagement_client_comments_one?.id) {
              throw new Error('The server did not confirm that the comment was saved.');
            }
            // Keep successful targets so retrying a partial save does not duplicate comments.
            completed.add(recordId);
            setSavedRecordIds(new Set(completed));
          }),
      );
      const failures = results.filter((result) => result.status === 'rejected');
      if (failures.length) {
        throw new AggregateError(
          failures.map((failure) => failure.reason),
          failures
            .map((failure) =>
              failure.reason instanceof Error ? failure.reason.message : 'An unexpected error occurred.',
            )
            .join(' '),
        );
      }
      onClose();
    } catch (cause) {
      const detail = cause instanceof Error ? cause.message : 'An unexpected error occurred.';
      setError(
        `Saved to ${completed.size} of ${recordIds.length} client records. ${detail} Retry to save to the remaining clients.`,
      );
    } finally {
      saving.current = false;
      setIsSaving(false);
    }
  };

  return (
    <BaseModal
      open
      onClose={() => {
        if (!saving.current) onClose();
      }}
      ariaLabelledBy="comment-modal-title"
      sx={{ maxHeight: 'calc(100dvh - 32px)', overflowY: 'auto' }}
    >
      <SelectedClientsList selectedClients={selectedClients} />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <CommentForm
        isSaving={isSaving}
        readOnly={savedRecordIds.size > 0}
        onCancel={onClose}
        onSave={(data) => void handleSave(data)}
      />
    </BaseModal>
  );
};

export default CommentModal;
